/**
 * Правила пароля при создании и смене (регистрация, восстановление) — паритет с нативкой
 * (`pages/Auth/constants/password.ts` супер-аппа, контракт `RegisterDto.password` бэкенда):
 * 12–128 символов, только латиница, цифры и `@ $ ! % * ? &`, обязательны буква, цифра и
 * спецсимвол. Для ВХОДА правило мягче (от 8 символов), см. Login.
 */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;
export const PASSWORD_PATTERN =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;

/** Чем новый пароль не подходит: длина или состав символов. */
export type TPasswordIssue = 'length' | 'pattern';

/** Ключи i18n с текстом ошибки под полем для каждой проблемы пароля. */
export const PASSWORD_ISSUE_MESSAGE_KEYS: Record<TPasswordIssue, string> = {
  length: 'password.short',
  pattern: 'password.invalid'
};

/**
 * Проверяет новый пароль. Длина проверяется первой: короткому паролю подсказка про
 * длину понятнее, чем про состав символов.
 * @returns проблема пароля или `null`, если пароль подходит
 */
export const getPasswordIssue = (value: string): TPasswordIssue | null => {
  if (value.length < PASSWORD_MIN || value.length > PASSWORD_MAX) {
    return 'length';
  }
  if (!PASSWORD_PATTERN.test(value)) return 'pattern';
  return null;
};

/** Требование к новому паролю для подсказки под полем. */
export type TPasswordRule =
  | 'length'
  | 'letter'
  | 'digit'
  | 'special'
  | 'allowed';

/** Ключи i18n с текстом каждого требования. */
export const PASSWORD_RULE_MESSAGE_KEYS: Record<TPasswordRule, string> = {
  length: 'password.rules.length',
  letter: 'password.rules.letter',
  digit: 'password.rules.digit',
  special: 'password.rules.special',
  allowed: 'password.rules.allowed'
};

/**
 * Какие требования пароль уже выполняет — для подсказки при вводе. Те же правила, что в
 * `getPasswordIssue`, разбитые по пунктам: пароль подходит, только когда выполнены все.
 * Для пустого значения не выполнен ни один пункт.
 */
export const getPasswordRules = (
  value: string
): { rule: TPasswordRule; passed: boolean }[] => [
  {
    rule: 'length',
    passed: value.length >= PASSWORD_MIN && value.length <= PASSWORD_MAX
  },
  { rule: 'letter', passed: /[A-Za-z]/.test(value) },
  { rule: 'digit', passed: /\d/.test(value) },
  { rule: 'special', passed: /[@$!%*?&]/.test(value) },
  { rule: 'allowed', passed: /^[A-Za-z\d@$!%*?&]+$/.test(value) }
];
